import { hasRoute } from '@common/utils'
import LanguageSwitcher from '@components/common/language-switcher'
import Logo from '@components/common/logo'
import CreditCounter from '@components/student-dashboard/credit-system/counter-cta'
import StudentDetailsModal from '@components/trial-class/join/student-details-modal.dynamic'
import TimezoneModal from '@components/trial-class/timezone-modal.dynamic'
import { useSessionContext } from '@contexts/session'
import { SUBJECTS } from '@lib/constants/billing'
import { NAVBAR_LINKS } from '@lib/constants/common'
import { SYSTEM_TIMEZONE } from '@lib/utils/date-native'
import { t, Trans } from '@lingui/macro'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'

const CoursesDropdown = dynamic(() =>
  import('@components/common/navbar/navbar-dropdowns/courses-dropdown')
)

const CampsDropdown = dynamic(() =>
  import('@components/common/navbar/navbar-dropdowns/camps-dropdown')
)

const SupportDropdown = dynamic(() =>
  import('@components/common/navbar/navbar-dropdowns/support-dropdown')
)

const UserDropdown = dynamic(() =>
  import('@components/common/navbar/navbar-dropdowns/user-dropdown')
)
const NavbarNewLink = dynamic(() =>
  import('@components/common/navbar-new-link')
)
const LoggedOutDesktopItems = dynamic(() =>
  import('@components/common/navbar/logged-out-desktop-items')
)

const {
  COMPETITIONS,
  DASHBOARD,
  REFERRAL,
  CODING_FOR_KIDS,
  JOIN_CLASS_LOGGED_IN,
  QUIZZES,
} = NAVBAR_LINKS

type Props = {
  loggedIn: boolean
  isQuizChampionship: any
  customCTAText: string
  customCTAonClick: () => void
  next: string
  isPaidUser: boolean
  trialClassCompleteLink: string
  username: string
  isDark: boolean
  showPartnerLink: boolean
  isTeacher: boolean
  timezone: string
  firstName: string
  tryFreeClassLink?: string
}

const DesktopNavbar = ({
  customCTAText,
  customCTAonClick,
  isPaidUser,
  isQuizChampionship,
  loggedIn,
  next,
  trialClassCompleteLink,
  username,
  isDark,
  showPartnerLink,
  isTeacher,
  timezone,
  firstName,
  tryFreeClassLink,
}: Props) => {
  const router = useRouter()
  const isDashboard = hasRoute(router.asPath, ['/**/dashboard/**'])
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    timezone || SYSTEM_TIMEZONE
  )
  const [isTimezoneModalOpen, setIsTimezoneModalOpen] = useState(false)
  const [isStudentDetailsModalOpen, setIsStudentDetailsModalOpen] = useState(
    false
  )
  const { totalCodingCredits, totalMathCredits } = useSessionContext()
  return (
    <>
      <div className="hidden md:flex md:flex-1 md:space-x-3 xl:space-x-5">
        <Link
          href={loggedIn ? '/dashboard/' : '/'}
          prefetch={loggedIn ? true : false}
        >
          <a className="flex items-center w-full" style={{ width: '7rem' }}>
            <Logo
              white={isDark ? true : false}
              withText
              className="cursor-pointer"
              style={{ width: '120px', height: '32px' }}
              alt={t`Codingal`}
            />
          </a>
        </Link>

        {!loggedIn && (
          <>
            <CoursesDropdown isDark={isDark} />
            <CampsDropdown isDark={isDark} />

            <Link href={COMPETITIONS.link} prefetch={false}>
              <a className="inline-block py-2 text-lg align-middle font-600 text-grey hover:text-orange">
                <Trans
                  id={COMPETITIONS.text?.id}
                  values={COMPETITIONS.text?.values}
                />
              </a>
            </Link>
            {isQuizChampionship && (
              <NavbarNewLink link={QUIZZES.link} text={QUIZZES.text} />
            )}
            <a
              href="https://www.codingal.com/coding-for-kids/blog/"
              className="inline-block py-2 text-lg align-middle font-600 text-grey hover:text-orange"
            >
              <Trans>Blog</Trans>
            </a>
            {showPartnerLink && (
              <Link href={REFERRAL.link} prefetch={false}>
                <a className="inline-block py-2 text-lg align-middle font-600 text-grey hover:text-orange">
                  <Trans
                    id={REFERRAL.text?.id}
                    values={REFERRAL.text?.values}
                  />
                </a>
              </Link>
            )}
          </>
        )}
        {loggedIn && (
          <>
            <CoursesDropdown isDark={isDark} />
            <Link href={COMPETITIONS.link}>
              <a
                className={`${
                  isDark ? 'text-white' : 'text-grey hover:text-orange'
                } inline-block text-lg font-500 md:font-600 align-middle py-2`}
              >
                <Trans
                  id={COMPETITIONS.text?.id}
                  values={COMPETITIONS.text?.values}
                />
              </a>
            </Link>

            {isQuizChampionship && (
              <NavbarNewLink
                link={QUIZZES.link}
                text={QUIZZES.text}
                isDark={isDark}
              />
            )}

            {isTeacher && (
              <Link href={CODING_FOR_KIDS.link}>
                <a className="inline-block py-2 text-lg align-middle md:font-600 text-grey hover:text-orange">
                  <Trans
                    id={CODING_FOR_KIDS.text?.id}
                    values={CODING_FOR_KIDS.text?.values}
                  />
                </a>
              </Link>
            )}

            {isDashboard && <SupportDropdown isDark={isDark} />}
          </>
        )}
      </div>
      <div className="ml-4 flex items-center space-x-4 py-2">
        {loggedIn && isPaidUser && (
          <div className="flex space-x-2">
            <CreditCounter
              totalCredits={totalMathCredits}
              subject={SUBJECTS.MATH}
            />
            <CreditCounter
              totalCredits={totalCodingCredits}
              subject={SUBJECTS.CODING}
            />
          </div>
        )}

        <LanguageSwitcher />

        {customCTAText && (
          <button
            onClick={customCTAonClick}
            className="inline-block py-2 ml-2 text-lg font-medium text-white align-middle rounded font-600 btn btn-orange focus:outline-none"
          >
            {customCTAText}
          </button>
        )}

        {!customCTAText && !loggedIn ? (
          <LoggedOutDesktopItems
            nextPage={next}
            tryFreeClassLink={tryFreeClassLink}
          />
        ) : (
          <>
            {isPaidUser || isTeacher ? (
              <Link href={DASHBOARD.link}>
                <a
                  className={`${
                    isDark ? 'text-white' : 'text-grey hover:text-orange'
                  } inline-block align-middle py-2 font-500 md:font-600 text-lg text-grey`}
                >
                  <Trans
                    id={DASHBOARD.text?.id}
                    values={DASHBOARD.text?.values}
                  />
                </a>
              </Link>
            ) : (
              router.pathname !== trialClassCompleteLink && (
                <Link href={JOIN_CLASS_LOGGED_IN.link} prefetch={false}>
                  <a
                    className={`${
                      isDark ? 'text-white' : 'text-grey hover:text-orange'
                    } inline-block align-middle py-2 font-500 md:font-600 text-lg text-grey`}
                  >
                    <Trans id={JOIN_CLASS_LOGGED_IN.text?.id} />
                  </a>
                </Link>
              )
            )}
            <span className="hidden mx-4 border border-grey-300 md:block" />
            <div className="flex items-center">
              <UserDropdown
                firstName={firstName}
                isDark={isDark}
                isTeacher={isTeacher}
                setIsStudentDetailsModalOpen={setIsStudentDetailsModalOpen}
                setIsTimezoneModalOpen={setIsTimezoneModalOpen}
                username={username}
              />
            </div>
            {!isTeacher && (
              // TODO: Add i18n support
              <StudentDetailsModal
                isOpen={isStudentDetailsModalOpen}
                setIsOpen={setIsStudentDetailsModalOpen}
                isMinimal={true}
              />
            )}
            {/* TODO: Add i18n support */}
            <TimezoneModal
              isOpen={isTimezoneModalOpen}
              setIsOpen={setIsTimezoneModalOpen}
              selectedTimezone={selectedTimezone}
              setSelectedTimezone={setSelectedTimezone}
              isReload={true}
            />
          </>
        )}
      </div>
    </>
  )
}

export default DesktopNavbar
